import { useEffect, useState } from "react";
import { X } from "lucide-react";

// Shared "title + short preview + See more…" cell used by the coord and teacher
// progress-track item rows. Keeps long hospitality descriptions from swallowing
// the row while still giving one click to read the whole thing.
const PREVIEW_CHARS = 55;

function truncate(text) {
  if (!text) return "";
  if (text.length <= PREVIEW_CHARS) return text;
  const soft = text.slice(0, PREVIEW_CHARS);
  const lastSpace = soft.lastIndexOf(" ");
  return `${soft.slice(0, lastSpace > 20 ? lastSpace : PREVIEW_CHARS).trimEnd()}\u2026`;
}

export function DescriptionCell({ title, description, testid }) {
  const [open, setOpen] = useState(false);
  const preview = truncate(description);
  const hasMore = description && description.length > preview.length - 1; // minus ellipsis
  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  return (
    <>
      <div className="text-xs leading-snug mt-1" style={{ color: "var(--ink-2)" }} data-testid={testid}>
        {preview}
        {hasMore && (
          <>
            {" "}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setOpen(true); }}
              className="underline text-[0.7rem]"
              style={{ color: "var(--terracotta)" }}
              data-testid={testid ? `${testid}-see-more` : undefined}
            >
              See more
            </button>
          </>
        )}
      </div>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center py-10 px-4 overflow-y-auto"
          style={{ background: "rgba(14,34,64,0.6)" }}
          onClick={() => setOpen(false)}
          data-testid={testid ? `${testid}-overlay` : undefined}
        >
          <div
            className="w-full max-w-md rounded-md"
            style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }}
            onClick={(e) => e.stopPropagation()}
            data-testid={testid ? `${testid}-modal` : undefined}
          >
            <div className="p-5 flex items-start justify-between gap-4 border-b" style={{ borderColor: "var(--border-subtle)" }}>
              <h3 className="font-serif-display text-xl leading-tight" style={{ color: "var(--ink)" }}>{title}</h3>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-full hover:bg-[var(--surface-hover)]"
                style={{ color: "var(--muted)" }}
                data-testid={testid ? `${testid}-close` : undefined}
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-5 text-sm leading-relaxed whitespace-pre-line" style={{ color: "var(--ink-2)" }}>
              {description}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
